import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { isEmpty, isNil } from 'lodash'
import { useInfiniteCompanies } from '@/shared/hooks/useCompanies'
import { flattenInfiniteData } from '@/shared/utils/queryUtils'
import { Company } from '@/shared/models/companyModel'
import { Equal, Regex } from '@hello-aria/query-string'
import { useMe } from '@/shared/hooks/useMe'
import Select, { SelectProps } from '@/shared/v2/shared/components/Select'

export type CompaniesSelectOptions = {
    value: Company['id']
    label: Company['name']
}

export type CompaniesSelectProps = SelectProps<CompaniesSelectOptions, false>

export const CompaniesSelect = (props: CompaniesSelectProps) => {
    const t = useTranslations('root')
    const { companyId, setCompanyId } = useMe()

    const [search, setSearch] = useState('')

    const regex = new RegExp(`.*${search}.*`, 'ig')
    const companies = useInfiniteCompanies({
        query: {
            filters: isEmpty(search) ? undefined : [Regex('name', String(regex))]
        }
    })

    const selectedCompanyData = useInfiniteCompanies({
        query: {
            filters: [Equal('id', companyId)]
        }
    })

    const flattenCompanies = flattenInfiniteData(companies.data)
    const firstCompany = flattenCompanies?.[0]

    useEffect(() => {
        if (!companyId) return

        const objectIdRegex = /^[0-9a-fA-F]{24}$/
        const isValidObjectId = objectIdRegex.test(companyId)

        if (!isValidObjectId && firstCompany) {
            setCompanyId(firstCompany.id)
            return
        }
    }, [companyId, firstCompany, setCompanyId])
    const onFetchNextUsers = () => {
        companies.fetchNextPage()
    }

    const onSearchUsers = (value: string) => {
        setSearch(value)
    }

    const onSelectCompany = (company: CompaniesSelectOptions | null) => {
        if (isNil(company)) {
            return
        }

        setCompanyId(company.value)
    }

    const areCompaniesLoading = companies.isFetchingNextPage || companies.isFetchingPreviousPage
    const selectedCompany = selectedCompanyData.data?.pages[0]?.body?.[0]

    return (
        <Select<CompaniesSelectOptions, false>
            state="default"
            className="w-[300px]"
            data-testid="__companies-select__"
            isLoading={companies.isFetching || areCompaniesLoading}
            onMenuScrollToBottom={onFetchNextUsers}
            onInputChange={onSearchUsers}
            options={flattenCompanies?.map((company) => ({
                value: company.id,
                label: company.name
            }))}
            value={
                isNil(selectedCompany)
                    ? null
                    : {
                          value: selectedCompany.id,
                          label: selectedCompany.name
                      }
            }
            onChange={(value) => onSelectCompany(value)}
            placeholder={t('components.companiesSelect.placeholder')}
            noOptionsMessage={() => t('components.companiesSelect.noOptionsMessage')}
            {...props}
        />
    )
}

export default CompaniesSelect
